import { useState } from 'react';
import { App, Button, Space, Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { TldCase, TldStateReport as Report } from '@/apis/simulation';
import ApiCodeLink from '@components/ApiCodeLink';
import { buildFoxgloveUrl, resolveMcapUrl } from '@/common/foxglove';
import SideInfo from './SideInfo';
import { EMPTY, fmtCount, fmtPp, fmtRatio } from '../format';

const directions: Record<string, string> = {
  vehicle_straight: '直行', vehicle_left: '左转', vehicle_right: '右转',
  vehicle_uturn: '掉头',
};

export default function TldStateReport({ report, onViewLog }: {
  report: Report; onViewLog: (taskId: number) => void;
}) {
  const m = report.metrics;
  const c = report.compare;
  const criteria = report.criteria;
  const { message } = App.useApp();
  const [replayTask, setReplayTask] = useState<number | null>(null);
  const openReplay = async (row: TldCase) => {
    if (!row.caseId) return;
    setReplayTask(row.taskId);
    try {
      const url = await resolveMcapUrl(row.taskId, row.caseId);
      if (url) window.open(buildFoxgloveUrl(url, row.taskId), '_blank');
      else message.warning('未找到对应的 mcap 文件');
    } catch {
      message.error('获取 mcap 链接失败');
    } finally {
      setReplayTask(null);
    }
  };
  const columns: ColumnsType<TldCase> = [
    { title: '场景', key: 'case', render: (_, r) => <ApiCodeLink code={r.apiCode} source={r.source} /> },
    { title: 'Case', dataIndex: 'caseId', render: (v) => v || EMPTY },
    { title: '达标结果', key: 'passed', render: (_, r) => (
      <Tag color={r.passed ? 'green' : 'red'}>{r.passed ? '达标' : '未达标'}</Tag>
    ) },
    { title: '颜色一致率', key: 'color', render: (_, r) => fmtRatio(r.metrics.colorConsistency) },
    { title: '同色 / 比较灯数', key: 'lights', render: (_, r) =>
      `${fmtCount(r.metrics.colorMatchLights)} / ${fmtCount(r.metrics.comparedLights)}` },
    { title: '时间对齐率', key: 'aligned', render: (_, r) => fmtRatio(r.metrics.alignedFrameRatio) },
    ...(c ? [{ title: '颜色 Δ（pp）', key: 'delta', render: (_: unknown, r: TldCase) => fmtPp(r.delta.colorConsistency) }] : []),
    { title: '下钻', key: 'log', render: (_, r) => <Space>
      <Button size="small" disabled={!r.caseId} loading={replayTask === r.taskId} onClick={() => openReplay(r)}>回放</Button>
      <Button size="small" onClick={() => onViewLog(r.taskId)}>日志</Button>
    </Space> },
  ];
  return (
    <>
      <div className="pe-report-card">
        <div className="pe-report-card-title">TLD 红绿灯状态评测</div>
        <div className="pe-sides">
          {c && <SideInfo header={c.base} side="base" />}
          <SideInfo header={report.header} side="current" />
        </div>
        <div className="pe-tld-notes">
          <p>指标型表示输出数值指标；达标结果是 checker 对每个 case 的阈值结论，不是任务运行状态。</p>
          <p>{criteria
            ? `本次判据：颜色一致率 ≥ ${fmtRatio(criteria.minColorAccuracy)}，时间对齐率 ≥ ${fmtRatio(criteria.minMatchedFrameRatio)}；相机时间与 GT 相差不超过 ${criteria.timeToleranceSec * 1000} ms。`
            : '本任务没有统一的已上报判据：旧报告可能缺少配置，各 case 也可能配置不同。当前实现默认颜色一致率 ≥ 90%、时间对齐率 ≥ 50%、最大时间差 50 ms；这不代表历史任务实际配置，具体以逐 case 配置和原始结果说明为准。'}
            {' '}还必须有可读取的 TLD 真值、感知输出、PnC 使用的灯类型，以及可对齐且有颜色标注的样本；缺失时也会未达标。</p>
          <p>{report.advisory === true
            ? '本任务的 TLD 阈值结论不参与仿真 case / Job 成功或失败的判定。'
            : report.advisory === false
              ? '本任务存在参与仿真结论的 TLD 结果，具体影响见逐 case 说明。'
              : '本任务未完整上报 TLD 结果是否参与仿真结论，不能据此推断 Job 状态。'}</p>
          <p>颜色一致率比较感知输出与 GT 的颜色，不是检测召回率；只比较本场景 PnC 使用的车辆灯类型，未检测出的灯不计为漏检。</p>
          <p>闪烁、倒计时只展示数值，不参与达标判定；倒计时允许误差{criteria ? ` ${criteria.countdownToleranceSec} 秒` : '由运行配置决定，源码默认为 1 秒'}。汇总先累加各 case 计数，再计算比率；不对汇总值重新判定。</p>
          {report.legacyCases > 0 && <p>含 {report.legacyCases} 个旧版报告：方向、闪烁、倒计时未结构化上报完整计数，当前汇总无法计算；原始说明可在逐 case 展开查看。空值不表示 0%，无样本也不表示正确。</p>}
        </div>
      </div>
      <div className="pe-report-card">
        <div className="pe-report-card-title">全部场景汇总 · {report.withMetrics} 个指标 case</div>
        <Table pagination={false} size="small" rowKey="name" dataSource={[
          { name: '颜色一致率', value: fmtRatio(m.colorConsistency), count: `${fmtCount(m.colorMatchLights)} / ${fmtCount(m.comparedLights)} 灯` },
          { name: '时间对齐率', value: fmtRatio(m.alignedFrameRatio), count: `${fmtCount(m.matchedMessages)} / ${fmtCount(m.keptMessages)} 条消息` },
          { name: '闪烁一致率', value: fmtRatio(m.blinkAgreement), count: `${fmtCount(m.blinkSame)} / ${fmtCount(m.blinkTotal)} 灯` },
          { name: '倒计时一致率', value: fmtRatio(m.countdownAgreement), count: `${fmtCount(m.countdownSame)} / ${fmtCount(m.countdownTotal)} 灯` },
          { name: '消息覆盖', value: `接收 ${fmtCount(m.tldMessages)}`, count: `空消息 ${fmtCount(m.emptyMessages)} · 类型过滤 ${fmtCount(m.filteredOutMessages)}` },
          { name: 'GT 帧覆盖', value: fmtCount(m.gtFrames), count: `匹配 ${fmtCount(m.matchedGtFrames)}` },
        ]} columns={[
          { title: '指标', dataIndex: 'name' }, { title: '结果', dataIndex: 'value' }, { title: '计数 / 覆盖', dataIndex: 'count' },
        ]} />
      </div>
      {c && <div className="pe-report-card">
        <div className="pe-report-card-title">基线对比 · 仅 {c.comparedCases} 个共同场景</div>
        <p>本次 {fmtRatio(c.currentMetrics.colorConsistency)} · 基线 {fmtRatio(c.baseMetrics.colorConsistency)} · 颜色一致率 Δ {fmtPp(c.delta.colorConsistency)}</p>
        <p>本次有指标 {report.withMetrics} / 基线有指标 {c.baseCases}；非共同场景不参与 Δ。</p>
      </div>}
      <div className="pe-report-card">
        <div className="pe-report-card-title">按方向 · 颜色一致率</div>
        <Table rowKey="direction" size="small" pagination={false} dataSource={report.byDirection} columns={[
          { title: '方向', dataIndex: 'direction', render: (v) => directions[v] || v },
          { title: '同色 / 比较灯数', key: 'count', render: (_, r) => `${fmtCount(r.metrics.colorMatchLights)} / ${fmtCount(r.metrics.comparedLights)}` },
          { title: '颜色一致率', key: 'ratio', render: (_, r) => fmtRatio(r.metrics.colorConsistency) },
        ]} />
        <div style={{ marginTop: 12 }}>颜色错配（GT → 预测）：{report.mismatches === null ? EMPTY :
          Object.entries(report.mismatches).map(([name, count]) => `${name}: ${fmtCount(count)}`).join(' · ') || '无'}</div>
      </div>
      <div className="pe-report-card">
        <div className="pe-report-card-title">逐 case · 未达标优先</div>
        <Table<TldCase> rowKey="sceneId" size="small" dataSource={report.cases} columns={columns}
          scroll={{ x: 1100 }} expandable={{ expandedRowRender: (r) => <>
            <div className="pe-tld-notes">
              {r.criteria && <p>本 case 判据：颜色一致率 ≥ {fmtRatio(r.criteria.minColorAccuracy)}，时间对齐率 ≥ {fmtRatio(r.criteria.minMatchedFrameRatio)}，最大时间差 {r.criteria.timeToleranceSec * 1000} ms。</p>}
              <p>{r.advisory === true ? '此结果不参与仿真 case / Job 成败判定。' : r.advisory === false ? '此结果参与仿真 case 成败判定。' : '此结果未上报是否参与仿真成败判定。'}</p>
            </div>
            <div style={{ whiteSpace: 'pre-wrap' }}>{r.messages.join('\n') || '无附加信息'}</div>
          </> }} />
      </div>
    </>
  );
}
